import { isPanelPath } from '@/lib/panel-path';

import { meetsRequirement } from './resolve-steps';
import { textOf, type StepContext, type TourDef } from './tours/types';

export interface FinishAction {
  /** The last step's primary button, instead of «متوجه شدم». */
  label: string;
  /** A same-origin panel path, may carry a query. */
  to: string;
}

/**
 * Pure: the finish button this viewer gets (critic C7), or `null` for a plain
 * «متوجه شدم». Shown only when `requires` holds, `when` (if any) agrees and
 * the label is not empty — a finish resolved per viewer (the core tour's
 * «ادامهٔ راه‌اندازی» / «بعدی: …») says «nothing for you» with an empty label.
 * A target that is not a panel path is refused: the rule is the same for
 * every navigation guidance makes.
 *
 * Everything a finish needs to decide comes from the step context — the
 * checklist's next open step is `ctx.nextSetupPath` (`useStepContext`).
 */
export function resolveFinish(
  tour: Pick<TourDef, 'finish'>,
  ctx: StepContext,
): FinishAction | null {
  const finish = tour.finish;
  if (!finish) return null;
  if (!meetsRequirement(finish.requires, ctx.permissions)) return null;
  if (finish.when && !finish.when(ctx)) return null;
  const to = typeof finish.to === 'function' ? finish.to(ctx) : finish.to;
  if (!isPanelPath(to)) return null;
  const label = textOf(finish.label, ctx).trim();
  return label ? { label, to } : null;
}

/** `resolveFinish`, memo-free: cheap, and the context is already memoised. */
export function useTourFinish(
  tour: Pick<TourDef, 'finish'>,
  ctx: StepContext,
): FinishAction | null {
  return resolveFinish(tour, ctx);
}
